import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import { components } from 'react-select';
import debounce from 'lodash/debounce';

// Hooks
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Helpers
import { isValidDomain } from 'Shared/helpers';

// Actions
import { updateOrganizationAction } from 'Shared/actions/organizations';

// Components
import { WpContainer, WpContainerBody, WpContainerHeader } from 'Wpds/components/container';
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import TextField from 'Shared/components/TextField/TextField';

// Constants
import Colors from 'Wpds/colors/ColorConstants';

// Styles
import './UserManagementOrganization.scss';

const selectStyles = {
  container: base => ({ ...base, maxWidth: '350px' }),
  multiValue: base => ({ ...base, backgroundColor: '#ABC3FD' }),
  multiValueLabel: base => ({ ...base, color: '#0238BB' }),
  multiValueRemove: base => ({
    ...base,
    cursor: 'pointer',
    ':hover': { color: '#0238BB', backgroundColor: '#ABC3FD' },
  }),
};

function UserManagementOrganization() {
  const dispatch = useDispatch();
  const { isOrgAdmin: isOrganizationRoleAdmin } = useCurrentUser();
  const {
    isPiOrganization,
    organization: { name, id, availableDomains },
  } = useOrganization();
  const [organizationName, setOrganizationName] = useState(name);

  const organizationDomainsOptions = availableDomains.map(domain => ({ value: domain, label: domain }));
  const [organizationDomainsValues, setOrganizationDomainsOptions] = useState(organizationDomainsOptions);

  useEffect(() => setOrganizationName(name), [name]);

  const debouncedUpdateOrganizationName = useCallback(
    debounce(({ name: organizationNameValue }) => {
      dispatch(updateOrganizationAction({ id, name: organizationNameValue }));
    }, 1000),
    []
  );

  const handleTextFieldChange = useCallback(
    event => {
      setOrganizationName(event.target.value);
      debouncedUpdateOrganizationName({ name: event.target.value });
    },
    [debouncedUpdateOrganizationName]
  );

  const handleOrganizationDomainChange = useCallback(
    options => {
      if (options) {
        const validOptions = options.filter(({ value }) => isValidDomain(value));
        const invalidOptions = options.filter(({ value }) => !isValidDomain(value));
        if (invalidOptions.length)
          return WpToastManager.danger(`Invalid domains: ${invalidOptions.map(({ value }) => value).join(', ')}`);
        setOrganizationDomainsOptions(validOptions);
        const validDomainValues = validOptions.map(({ value }) => value);
        return dispatch(updateOrganizationAction({ id, availableDomains: validDomainValues }));
      }
      setOrganizationDomainsOptions([]);
      return dispatch(updateOrganizationAction({ id, availableDomains: [] }));
    },
    [dispatch, id]
  );

  return (
    <WpContainer className="UserManagementOrganization">
      <WpContainerHeader>
        <h5>Organization</h5>
      </WpContainerHeader>
      <WpContainerBody>
        <div className="d-flex flex-column">
          {!isPiOrganization && isOrganizationRoleAdmin ? (
            <TextField
              label="Organization Name"
              name="organizationName"
              type="text"
              wrapperClassName="UserManagementOrganization__input"
              value={organizationName}
              onChange={handleTextFieldChange}
            />
          ) : (
            <>
              <div className="UserManagementOrganization__label text-muted mb-2">Organization Name</div>
              <span>{organizationName}</span>
            </>
          )}
          {!isPiOrganization && (
            <>
              <div className="UserManagementOrganization__label text-muted mt-3 mb-2">
                Allow users from the following domains to join this org
              </div>
              {isOrganizationRoleAdmin ? (
                <CreatableSelect
                  isClearable
                  isMulti
                  styles={selectStyles}
                  value={organizationDomainsValues}
                  options={organizationDomainsOptions}
                  onChange={handleOrganizationDomainChange}
                  components={{
                    DropdownIndicator: null,
                    ClearIndicator: props => (
                      <components.ClearIndicator {...props}>
                        <WpButton variant="tertiary" size="sm">
                          <WpIcon name="times-circle" />
                        </WpButton>
                      </components.ClearIndicator>
                    ),
                    MultiValueRemove: props => (
                      <components.MultiValueRemove {...props}>
                        <WpIcon name="times-circle" color={Colors.blue[900]} />
                      </components.MultiValueRemove>
                    ),
                  }}
                />
              ) : (
                <div className="d-flex">
                  {organizationDomainsValues.map(({ value }, index) => (
                    <span className="mr-1" key={value}>
                      {`${value}${index + 1 === organizationDomainsValues.length ? '' : ', '}`}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </WpContainerBody>
    </WpContainer>
  );
}

export default UserManagementOrganization;
